/**
 * V2.0.81 — Chat controller extracted from Editor.jsx.
 *
 * Consolidates the two heaviest chat-flow functions:
 *   - `runChatTurn(text, opts)` — single round-trip. Injects a temp
 *     user message, calls the NDJSON streaming endpoint, and rehydrates
 *     the design (blueprint + chat history + metadata) from the final
 *     `result` frame.
 *   - `handleSend(text, opts)` — the whole "user hit enter" flow:
 *     watchdog, silent retry cascade, auto-continue, thumbnail
 *     persist, suggested-actions, toast + Undo action, fixture-diff
 *     analytics, and error/504 fallback to Quick Mode.
 *
 * The Editor.jsx wrap-up bundle is ~20 dependencies (state atoms,
 * setters, refs, helper fns). Rather than passing 20 props we bundle
 * them into a single `ctx` object; the caller destructures the return
 * value. This is deliberately a "big context" hook — it's the same
 * shape as before, just no longer inlined in the page component.
 *
 * The V2.0.72 silent-retry cascade is preserved verbatim: 3 attempts,
 * each tightening the prompt (lowDetail → simpleMode) on recoverable
 * failure (network / 5xx / validator invalid_reason).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { sendChat, postAnalyticsEvent } from "../lib/api";
import { computeBlueprintDiff } from "../lib/blueprintDiff";
import { emitInteractionEvent } from "../lib/interactionTelemetry";
import {
  advanceBuildQueue,
  completeFinalPhase,
  countBlueprintRooms,
  diffFixtures,
  looksLikeDeleteIntent,
} from "../lib/editorHelpers";

const CHAT_WATCHDOG_MS = 180_000;
const RETRY_MAX = 3;

function shouldRetry(err, dataWithInvalidReason) {
  if (dataWithInvalidReason) return true;
  const status = err?.response?.status;
  if (!status) return true; // network / stream error — retry
  if (status >= 500) return true;
  return false;
}

export default function useChatController({
  designId,
  design,
  setDesign,
  simpleMode,
  quickMode,
  setQuickMode,
  setQuickModeFlash,
  setSuggestedActions,
  setBuildQueue,
  setContinuousZone,
  setShowSnapshotChip,
  setSnapshotSaved,
  persistThumbnail,
  undoLastChangeRef,
  renderRetryRef,
}) {
  const [chatLoading, setChatLoading] = useState(false);
  const previousBlueprintRef = useRef(null);
  const chatLoadingTimeoutRef = useRef(null);

  // Ref mirrors of props that runChatTurn / handleSend read at call
  // time — keeps their useCallback deps small so the callback ref
  // itself is stable across renders (important because Editor's chat
  // panel wires `handleSend` as a prop and unstable refs cause
  // spurious re-renders of the chat tree).
  const designRef = useRef(design);
  designRef.current = design;

  useEffect(
    () => () => {
      if (chatLoadingTimeoutRef.current) clearTimeout(chatLoadingTimeoutRef.current);
    },
    [],
  );

  const runChatTurn = useCallback(
    async (text, { lowDetail = false, simpleMode: simpleModeOverride } = {}) => {
      const tempUserMsg = {
        id: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        role: "user",
        content: text,
      };
      if (!previousBlueprintRef.current) {
        const bp = designRef.current?.blueprint_json;
        previousBlueprintRef.current = bp ? JSON.parse(JSON.stringify(bp)) : null;
      }
      setDesign((d) => ({
        ...d,
        chat_history: [...(d?.chat_history || []), tempUserMsg],
      }));
      const sendOpts = { lowDetail };
      if (simpleModeOverride !== undefined) sendOpts.simpleMode = simpleModeOverride;
      const r = await sendChat(designId, text, sendOpts);
      setDesign((d) => {
        const history = (d.chat_history || []).filter((m) => m.id !== tempUserMsg.id);
        history.push(r.data.user_message);
        history.push(r.data.assistant_message);
        const meta = r.data.metadata_updated || {};
        return {
          ...d,
          chat_history: history,
          blueprint_json: r.data.blueprint_json,
          ...meta,
        };
      });
      return r.data;
    },
    [designId, setDesign],
  );

  const handleSend = useCallback(
    async (text, opts = {}) => {
      const source = opts.source || null;
      const promptStartMs = Date.now();
      let attemptsUsed = 0;
      let outcomeMeta = null;
      setChatLoading(true);
      setSuggestedActions([]);
      setBuildQueue([]);
      setContinuousZone(null);
      setShowSnapshotChip(false);
      setSnapshotSaved(false);
      previousBlueprintRef.current = null; // fresh batch
      if (chatLoadingTimeoutRef.current) clearTimeout(chatLoadingTimeoutRef.current);
      chatLoadingTimeoutRef.current = setTimeout(() => {
        console.warn("[Editor] chatLoading watchdog fired — auto-resetting");
        setChatLoading(false);
        setContinuousZone(null);
      }, CHAT_WATCHDOG_MS);

      const runWithCascade = async () => {
        let lastErr = null;
        for (let attempt = 1; attempt <= RETRY_MAX; attempt++) {
          attemptsUsed = attempt;
          const turnOpts = { lowDetail: !!quickMode };
          if (attempt >= 2) turnOpts.lowDetail = true;
          if (attempt >= 3) turnOpts.simpleMode = true;
          try {
            const d = await runChatTurn(text, turnOpts);
            if (d && d.invalid_reason && attempt < RETRY_MAX) {
              console.warn(
                `[Editor] chat attempt ${attempt} rejected by validator: ${d.invalid_reason} — retrying tighter`,
              );
              lastErr = null;
              continue;
            }
            if (attempt > 1) {
              console.info(`[Editor] chat succeeded on attempt ${attempt}/${RETRY_MAX}`);
            }
            return d;
          } catch (err) {
            lastErr = err;
            if (attempt >= RETRY_MAX || !shouldRetry(err, false)) throw err;
            console.warn(
              `[Editor] chat attempt ${attempt} failed (${err?.response?.status || "net"}) — retrying tighter`,
              err?.response?.data?.detail || err?.message,
            );
          }
        }
        if (lastErr) throw lastErr;
      };

      try {
        let data = await runWithCascade();
        if (data && data.invalid_reason) {
          toast.error(
            "The AI's edit produced an invalid layout — your previous plan was kept. Try breaking the change into smaller steps.",
            { duration: 10000 },
          );
        }        const initialQueue = Array.isArray(data?.build_queue) ? data.build_queue : [];
        if (initialQueue.length > 0) setBuildQueue(initialQueue);
        if (data && data.auto_continue && data.auto_continue_prompt) {
          setContinuousZone(data.auto_continue_zone || "next zone");
          setBuildQueue((q) => advanceBuildQueue(q));
          try {
            data = await runChatTurn(data.auto_continue_prompt);
          } catch (e2) {
            console.warn("[Editor] auto-continue failed:", e2);
          }
        }
        setBuildQueue((q) => completeFinalPhase(q));
        setTimeout(persistThumbnail, 600);
        const actions = Array.isArray(data?.suggested_actions) ? data.suggested_actions : [];
        setSuggestedActions(actions);
        if (simpleMode) setShowSnapshotChip(true);

        const undoBlueprint = previousBlueprintRef.current;
        if (undoBlueprint) {
          const before = countBlueprintRooms(undoBlueprint);
          const after = countBlueprintRooms(data?.blueprint_json);
          const removedCount = Math.max(0, before - after);
          const userMentionedDelete = looksLikeDeleteIntent(text);
          const isDeletion = removedCount > 0 || (userMentionedDelete && after < before + 1);
          const fxDiff = diffFixtures(undoBlueprint, data?.blueprint_json);
          if (fxDiff.added.length > 0) {
            fxDiff.added.forEach((type) => {
              postAnalyticsEvent({ design_id: designId, event: "fixture.added", meta: { type } }).catch(() => {});
            });
          }
          if (fxDiff.removed.length > 0) {
            fxDiff.removed.forEach((type) => {
              postAnalyticsEvent({ design_id: designId, event: "fixture.removed", meta: { type } }).catch(() => {});
            });
          }
          const fxLabels = {
            fridge: "fridge", range: "range", dishwasher: "dishwasher",
            sink: "sink", kitchen_island: "island", cabinets: "cabinetry",
            pantry: "pantry", toilet: "toilet", vanity: "vanity",
            shower: "shower", tub: "tub", washer: "washer", dryer: "dryer",
            water_heater: "water heater",
          };
          const fxAddedHuman = fxDiff.added.map((t) => fxLabels[t] || t).filter(Boolean);
          if (isDeletion && removedCount > 0) {
            const noun = removedCount === 1 ? "room" : "rooms";
            toast(`Removed ${removedCount} ${noun}`, {
              duration: 8000,
              action: {
                label: "Undo delete",
                onClick: () => undoLastChangeRef.current?.(undoBlueprint),
              },
              className: "blueprint-toast-destructive",
            });
          } else if (fxAddedHuman.length > 0) {
            const sample = fxAddedHuman.slice(0, 4);
            const more = fxAddedHuman.length > sample.length
              ? ` + ${fxAddedHuman.length - sample.length} more`
              : "";
            toast(`Added ${sample.join(" + ")}${more}`, {
              duration: 7000,
              action: {
                label: "Undo",
                onClick: () => undoLastChangeRef.current?.(undoBlueprint),
              },
            });
          } else if (source === "auto-resolve" && opts.count) {
            const n = opts.count;
            toast(`Resolved ${n} code violation${n === 1 ? "" : "s"}`, {
              duration: 9000,
              action: {
                label: "Undo Auto-Resolve",
                onClick: () => undoLastChangeRef.current?.(undoBlueprint),
              },
            });
          } else {
            const diff = computeBlueprintDiff(undoBlueprint, data?.blueprint_json);
            const label = diff.hasChanges && diff.summary ? `AI ${diff.summary}` : "Design updated";
            toast(label, {
              duration: 7000,
              action: {
                label: "Undo",
                onClick: () => undoLastChangeRef.current?.(undoBlueprint),
              },
            });
          }
        }
        // V2.0.82 — Prompt outcome metadata for the tool-usage heatmap.
        // Set here (end of success path) so the `finally` block below
        // emits it. `has_changes` distinguishes "AI actually edited
        // geometry" from "no-op detected" turns.
        const diffFinal = undoBlueprint
          ? computeBlueprintDiff(undoBlueprint, data?.blueprint_json)
          : { hasChanges: !!data?.blueprint_json, summary: "" };
        outcomeMeta = {
          invalid_reason: !!data?.invalid_reason,
          has_changes: !!diffFinal.hasChanges,
          diff_kind: diffFinal.summary || (data?.invalid_reason ? "rejected" : "none"),
          auto_continued: !!data?.auto_continue,
        };
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const realMessage = detail || e?.message || "Architect AI failed";
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/client-errors`;
          fetch(url, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: `[chat-failed] ${realMessage}`,
              stack: `status=${status || "net"} detail=${detail || "(none)"} prompt="${(text || "").slice(0, 200)}"`,
              component_stack: "runWithCascade after 3 attempts",
              url: typeof window !== "undefined" ? window.location.href : "",
              user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
            }),
          }).catch(() => { /* network down — nothing we can do */ });
        } catch { /* swallow */ }
        if (status === 504 || status === 524) {
          if (!quickMode) {
            setQuickMode(true);
            try { localStorage.setItem("blueprintai.quickMode", "1"); } catch { /* noop */ }
            setQuickModeFlash(true);
            setTimeout(() => setQuickModeFlash(false), 2500);
          }
          toast.error(
            realMessage.includes("taking too long")
              ? realMessage
              : "The architect is taking too long — Quick Mode auto-enabled. Re-submit your prompt.",
            { duration: 8000 },
          );
        } else {
          toast.error(
            `Architect AI failed — ${realMessage}. Please try rephrasing the request in smaller steps, or click Undo.`,
            { duration: 10000 },
          );
        }
        setDesign((d) => ({
          ...d,
          chat_history: (d.chat_history || []).filter((m) => !m.id?.startsWith?.("temp_")),
        }));
      } finally {
        if (chatLoadingTimeoutRef.current) {
          clearTimeout(chatLoadingTimeoutRef.current);
          chatLoadingTimeoutRef.current = null;
        }
        setChatLoading(false);
        setContinuousZone(null);
        renderRetryRef.current = { lastPrompt: text };
        // V2.0.82 — Prompt outcome telemetry. `outcomeMeta` is set on
        // the success path below (right before the toast); if we hit
        // the catch block, it stays null and we emit an "error"
        // outcome. `attemptsUsed` reflects the retry cascade depth.
        emitInteractionEvent({
          tool: "chat",
          action: outcomeMeta ? "prompt_ok" : "prompt_error",
          ms: Date.now() - promptStartMs,
          committed: !!outcomeMeta && !outcomeMeta.invalid_reason,
          meta: {
            prompt_len: (text || "").length,
            retries: attemptsUsed,
            source,
            quick_mode: !!quickMode,
            simple_mode: !!simpleMode,
            ...(outcomeMeta || {}),
          },
        });
      }
    },
    [
      runChatTurn,
      simpleMode,
      quickMode,
      designId,
      setDesign,
      setSuggestedActions,
      setBuildQueue,
      setContinuousZone,
      setShowSnapshotChip,
      setSnapshotSaved,
      setQuickMode,
      setQuickModeFlash,
      persistThumbnail,
      undoLastChangeRef,
      renderRetryRef,
    ],
  );

  return useMemo(
    () => ({
      chatLoading,
      setChatLoading,
      previousBlueprintRef,
      chatLoadingTimeoutRef,
      runChatTurn,
      handleSend,
    }),
    [chatLoading, runChatTurn, handleSend],
  );
}
